import React from "react";
import { Badge, Progress } from "reactstrap";
import { formatCurrency } from "../../../../../utils/format";
import { MEMBERSHIP_STATUS_STYLES, MEMBERSHIP_DURATION_LABELS } from "../../constants";

/**
 * getMembershipColumns
 * Retorna a definição de colunas para a tabela de memberMemberships
 * (instâncias de plano adquirido pelo membro).
 *
 * Campos exibidos com base no modelo memberMembershipDefaults:
 * - membershipName, durationType, duration
 * - startDate, endDate + barra de progresso
 * - price, originalValue, discount, isScholarship
 * - status (active | suspended | canceled | expired)
 * - Ícone de visualizar → abre MembershipActionsModal
 *
 * @param {Function} formatDate - Formata YYYY-MM-DD para DD/MM/YYYY
 * @param {Function} onViewDetails - Callback ao clicar no ícone de visualizar
 * @param {Function} onPrintReceipt - Callback ao clicar no ícone de recibo
 */
const getMembershipColumns = ({ formatDate, onViewDetails, onPrintReceipt }) => [
    {
        label: "#",
        key: "index",
        style: { width: "40px" },
        render: (row, index, { page, rowsPerPage }) => (
            <span className="text-muted fw-bold">
                {(page - 1) * rowsPerPage + index + 1}
            </span>
        )
    },
    {
        label: "Plano",
        key: "membershipName",
        render: (row) => {
            return (
                <div className="d-flex flex-column gap-1">
                    <span className="fw-bold text-dark font-size-13">
                        {row.membershipName || "Plano sem nome"}
                    </span>
                    <span className="text-muted font-size-12">
                        <i className="mdi mdi-timer-sand me-1"></i>
                        {MEMBERSHIP_DURATION_LABELS[row.durationType] ? MEMBERSHIP_DURATION_LABELS[row.durationType](row.duration) : `${row.duration} período(s)`}
                    </span>
                </div>
            );
        }
    },
    {
        label: "Vigência",
        key: "startDate",
        render: (row) => {
            const start = row.startDate;
            const end = row.endDate;

            let progressPercent = 0;
            if (start && end && row.status === "active") {
                const now = new Date();
                const s = new Date(start + "T00:00:00");
                const e = new Date(end + "T23:59:59");
                const total = e - s;
                const elapsed = now - s;
                progressPercent = total > 0 ? Math.min(100, Math.max(0, Math.round((elapsed / total) * 100))) : 0;
            }

            return (
                <div className="d-flex flex-column gap-1">
                    <div className="d-flex align-items-center gap-2 font-size-12">
                        <span>
                            <i className="mdi mdi-calendar-start me-1 text-success"></i>
                            {formatDate(start)}
                        </span>
                        <span className="text-muted">→</span>
                        <span>
                            <i className="mdi mdi-calendar-end me-1 text-danger"></i>
                            {formatDate(end)}
                        </span>
                    </div>
                    {row.status === "active" && (
                        <Progress
                            value={progressPercent}
                            className="mt-1"
                            style={{ height: "4px" }}
                            color={progressPercent > 80 ? "warning" : "primary"}
                        />
                    )}
                </div>
            );
        }
    },
    {
        label: "Valor",
        key: "price",
        render: (row) => {
            const hasDiscount = row.discount > 0;
            const value = Number(row.price || 0);
            return (
                <div className="d-flex flex-column gap-0">
                    <span className="fw-bold text-dark font-size-13">
                        {formatCurrency(value)}
                    </span>
                    {hasDiscount && (
                        <span className="text-muted font-size-11 text-decoration-line-through">
                            {formatCurrency(row.originalValue)}
                        </span>
                    )}
                    {row.isScholarship && (
                        <Badge color="info" className="font-size-10 px-1 py-0 mt-1" pill style={{ width: 'fit-content' }}>
                            <i className="mdi mdi-school me-1"></i>Bolsista
                        </Badge>
                    )}
                </div>
            );
        }
    },
    {
        label: "Status",
        key: "status",
        render: (row) => {
            const st = MEMBERSHIP_STATUS_STYLES[row.status] || MEMBERSHIP_STATUS_STYLES.expired;
            return (
                <div className="d-flex flex-row gap-2 align-items-center">
                    <Badge color={st.color} className="font-size-12 px-2 py-1" pill>
                        <i className={`mdi ${st.icon} me-1`}></i>
                        {st.label}
                    </Badge>
                    
                    {row.status === "active" && row.isScheduledCancel && row.cancelDate && (
                        <Badge color="soft-danger" className="text-danger font-size-10 px-2 py-0 border border-danger border-opacity-25" pill>
                            <i className="mdi mdi-clock-alert-outline me-1"></i>
                            Cancela em {formatDate(row.cancelDate)}
                        </Badge>
                    )}
                </div>
            );
        }
    },
    {
        label: "",
        key: "actions",
        headerClassName: "text-end",
        className: "text-end",
        style: { width: "50px" },
        render: (row) => (
            <div className="d-flex justify-content-end gap-1">
                {row.idSale && (
                    <button
                        type="button"
                        className="btn btn-soft-secondary btn-sm rounded-0 d-inline-flex align-items-center justify-content-center"
                        title="Ver recibo"
                        onClick={() => onPrintReceipt && onPrintReceipt(row)}
                        style={{ width: "32px", height: "32px", padding: 0 }}
                    >
                        <i className="mdi mdi-receipt font-size-16"></i>
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-soft-info btn-sm rounded-0 d-inline-flex align-items-center justify-content-center"
                    title="Gerenciar plano"
                    onClick={() => onViewDetails(row)}
                    style={{ width: "32px", height: "32px", padding: 0 }}
                >
                    <i className="mdi mdi-eye-outline font-size-16"></i>
                </button>
            </div>
        )
    }
];

export default getMembershipColumns;
